import React, { useState, useEffect } from 'react';
import { Truck, MapPin, Phone, Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import './VehiclesPage.css';
import AddVehicleModal from './AddVehicleModal';
import EditVehicleModal from './EditVehicleModal';
import VehicleDetailsPanel from '../../../components/shared/VehicleDetailsPanel/VehicleDetailsPanel';
import axios from 'axios';
import cacheStorage from '../../../utils/cacheStorage';

const VehiclesHeader = ({ onAddVehicle }) => (
    <div className="vehicles-header-container">
      <div className="vehicles-header-titles">
        <h1 className="vehicles-title">Vehicles</h1>
        <p className="vehicles-subtitle">Manage fleet vehicles and maintenance</p>
      </div>
      <div className="vehicles-header-actions">
        <button className="vehicles-add-button" onClick={onAddVehicle}>
          <Plus size={20} />
          Add Vehicle
        </button>
      </div>
    </div>
);

const VehicleCard = ({ vehicle, onClick }) => {
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'status-active';
      case 'maintenance': return 'status-maintenance';
      case 'inactive': return 'status-inactive';
      case 'idle': return 'status-idle';
      default: return 'status-idle';
    }
  };

  const getStatusDotClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'dot-active';
      case 'maintenance': return 'dot-maintenance';
      case 'inactive': return 'dot-inactive';
      case 'idle': return 'dot-idle';
      default: return 'dot-idle';
    }
  };

  return (
      <div className="vehicle-card" onClick={onClick} style={{ cursor: 'pointer' }}>
        <div className="vehicle-card-header">
          <div className="vehicle-card-header-left">
            <div className="vehicle-icon-container">
              <Truck className="vehicle-icon" size={24} />
            </div>
            <div className="vehicle-info">
              <h3 className="vehicle-id">{vehicle.id}</h3>
              <p className="vehicle-type">{vehicle.type}</p>
            </div>
          </div>
          <div className={`vehicle-status-dot ${getStatusDotClass(vehicle.status)}`}></div>
        </div>

        <div className={`vehicle-status-badge ${getStatusClass(vehicle.status)}`}>
          {vehicle.status?.toUpperCase() || 'UNKNOWN'}
        </div>

        <div className="vehicle-details">
          <div className="vehicle-detail-row">
            <Truck className="vehicle-detail-icon" size={16} />
            <div className="vehicle-detail-text">
              <p className="vehicle-detail-label">Capacity</p>
              <p className="vehicle-detail-value">
                {vehicle.capacity_kg !== null && vehicle.capacity_kg !== undefined ? `${vehicle.capacity_kg} kg` : 'N/A'} / {vehicle.capacity_m3 !== null && vehicle.capacity_m3 !== undefined ? `${vehicle.capacity_m3} m³` : 'N/A'}
              </p>
            </div>
          </div>

          <div className="vehicle-detail-row">
            <MapPin className="vehicle-detail-icon" size={16} />
            <div className="vehicle-detail-text">
              <p className="vehicle-detail-label">Assigned Location</p>
              <p className="vehicle-detail-value">{vehicle.currentRoute || 'N/A'}</p>
            </div>
          </div>

          <div className="vehicle-detail-row">
            <Phone className="vehicle-detail-icon" size={16} />
            <div className="vehicle-detail-text">
              <p className="vehicle-detail-label">Assigned Courier</p>
              <p className="vehicle-detail-value">{vehicle.assignedCourier?.name || vehicle.courier || 'Unassigned'}</p>
              {(vehicle.assignedCourier?.phone || vehicle.phone) && (vehicle.assignedCourier?.phone !== 'N/A' && vehicle.phone !== 'N/A') && (
                  <p className="vehicle-detail-subvalue">{vehicle.assignedCourier?.phone || vehicle.phone}</p>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

const VehiclesGrid = ({ vehicles, onVehicleClick }) => {
  if (vehicles.length === 0) {
    return <div className="vehicles-empty-state">No vehicles found.</div>;
  }

  return (
      <div className="vehicles-grid">
        {vehicles.map((vehicle) => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} onClick={() => onVehicleClick(vehicle)} />
        ))}
      </div>
  );
};

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleToEdit, setVehicleToEdit] = useState(null);

  useEffect(() => {
    if (searchQuery && vehicles.length > 0) {
      const match = vehicles.find(v => v.id === searchQuery);
      if (match) {
        setSelectedVehicle(match);
      }
    }
  }, [searchQuery, vehicles]);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        setLoading(true);
        const token = cacheStorage.getItem('access_token');
        const response = await axios.get('https://smart-cargo-backend-923182991420.us-central1.run.app/api/vehicles', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const mappedData = response.data.map(v => ({
          id: v.plate_no,
          type: v.model,
          capacity_kg: v.capacity_kg || null,
          capacity_m3: v.capacity_m3 || null,
          warehouse_id: v.warehouse_id || null,
          status: v.status || 'INACTIVE',
          currentRoute: v.warehouse_id ? `Warehouse ${v.warehouse_id}` : 'N/A',
          assignedCourier: null
        }));

        setVehicles(mappedData);
        setError(null);
      } catch (err) {
        console.error('Failed to load vehicles:', err);
        setError('Failed to load vehicles. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchVehicles();
  }, []);

  const filteredVehicles = vehicles.filter(
      (vehicle) =>
          vehicle.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          vehicle.type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (vehicle.assignedCourier?.name || vehicle.courier || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddVehicle = () => {
    setIsModalOpen(true);
  };

  const handleAddSuccess = (newVehicle) => {
    setVehicles([...vehicles, newVehicle]);
  };

  const handleEdit = (data) => {
    setVehicleToEdit(data);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = (updatedVehicle) => {
    setVehicles(prev =>
        prev.map(v => v.id === updatedVehicle.id ? updatedVehicle : v)
    );
    setSelectedVehicle(updatedVehicle);
  };

  return (
      <div className="vehicles-page">
        <VehiclesHeader onAddVehicle={handleAddVehicle} />

        {loading ? (
            <div className="vehicles-loading">Loading vehicles...</div>
        ) : error ? (
            <div className="vehicles-error">{error}</div>
        ) : (
            <VehiclesGrid vehicles={filteredVehicles} onVehicleClick={setSelectedVehicle} />
        )}

        <AddVehicleModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onAddSuccess={handleAddSuccess}
        />

        <EditVehicleModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            vehicleData={vehicleToEdit}
            onEditSuccess={handleEditSuccess}
        />

        <VehicleDetailsPanel
            isOpen={!!selectedVehicle}
            onClose={() => setSelectedVehicle(null)}
            vehicleData={selectedVehicle}
            onEdit={handleEdit}
        />
      </div>
  );
}